import React, { useCallback } from 'react';
import { motion, type Variants } from 'framer-motion';
import { StatsCards } from './components/StatsCards';
import { SitesTable } from './components/SitesTable';
import { IncidentsList } from './components/IncidentsList';
import { ToastContainer } from './components/ToastContainer';
import { useDashboard } from './hooks/useDashboard';
import { useToast } from './hooks/useToast';
import type { FilterType } from './types';

const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.08,
            delayChildren: 0.05
        }
    }
};

const itemVariants: Variants = {
    hidden: { opacity: 0, y: 16 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.4,
            ease: [0.25, 0.46, 0.45, 0.94] as const
        }
    }
};

const fadeInVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { 
        opacity: 1,
        transition: { duration: 0.3 }
    }
};

export const DashboardPage: React.FC = () => {
    const { filteredSites, 
        stats, 
        activeIncidents, 
        isLoading, 
        error,
        refreshData,
        filter,
        setFilter
    } = useDashboard();
    const { toasts, addToast, removeToast } = useToast();

    const handleRefresh = useCallback(async () => {
        addToast('Обновление данных...', 'success');
        try {
            await refreshData();
            addToast('Данные успешно обновлены', 'success');
        } catch (error) {
            addToast('Ошибка при обновлении данных', 'error');
        }
    }, [addToast, refreshData]);

    const handleFilterChange = useCallback((newFilter: FilterType) => {
        setFilter(newFilter);
    }, [setFilter]);

    const currentTime = new Date().toLocaleTimeString('ru-RU', { 
        hour: '2-digit', 
        minute: '2-digit' 
    });

    if (isLoading && filteredSites.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
                <div className="w-8 h-8 border-2 border-[var(--brand)] border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-[var(--text-muted)]">Загрузка данных мониторинга...</p>
            </div>
        );
    }

    if (error && filteredSites.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 px-4">
                <div className="w-12 h-12 rounded-xl bg-[var(--status-down-bg)] flex items-center justify-center text-[var(--status-down)]">
                    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="15" y1="9" x2="9" y2="15"/>
                        <line x1="9" y1="9" x2="15" y2="15"/>
                    </svg>
                </div>
                <div className="text-center">
                    <p className="text-sm font-medium text-[var(--text)] mb-1">Не удалось загрузить данные</p>
                    <p className="text-xs text-[var(--text-muted)] mb-4">{error}</p>
                    <button
                        onClick={handleRefresh}
                        className="px-4 py-2 rounded-lg bg-[var(--brand)] text-white text-xs font-medium hover:opacity-90 transition-opacity"
                    >
                        Повторить
                    </button>
                </div>
            </div>
        )
    }

    return (
        <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="pb-8 min-h-full"
        >
            <motion.div 
                variants={itemVariants} 
                className="px-[18px] pt-4 pb-2.5"
            >
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                        <h1 className="text-[17px] font-semibold tracking-[-0.3px] text-[var(--text)] mb-1">
                            Обзор системы
                        </h1>
                        <p className="text-[11px] text-[var(--text-muted)] flex items-center gap-2">
                            <span className={`w-1.5 h-1.5 rounded-full ${activeIncidents.length > 0 ? 'bg-[var(--status-warn)]' : 'bg-[var(--status-up)]'} animate-pulse`} />
                            {activeIncidents.length > 0 
                                ? `${activeIncidents.length} активных проблем` 
                                : 'Все системы работают нормально'}
                        </p>
                    </div>
                    
                    <motion.button
                        whileHover={{ scale: 1.02, y: -1 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={handleRefresh}
                        disabled={isLoading}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--brand)] text-white text-xs font-medium 
                                 hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <svg 
                            className={isLoading ? 'animate-spin' : ''} 
                            width="14" 
                            height="14" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            viewBox="0 0 24 24"
                        >
                            <polyline points="23 4 23 10 17 10"/>
                            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                        </svg>
                        <span className="hidden sm:inline">
                            {isLoading ? 'Обновление...' : 'Обновить'}
                        </span>
                    </motion.button>
                </div>
            </motion.div>

            <motion.div variants={itemVariants}>
                <StatsCards stats={stats} />
            </motion.div>

            <motion.section 
                variants={itemVariants} 
                className="mt-6"
                aria-label="Состояние сайтов"
            >
                <div className="flex items-center justify-between gap-3 px-[18px] mb-3">
                    <div className="flex items-center gap-3">
                        <h2 className="text-[13px] font-medium text-[var(--text)]">
                            Состояние сайтов
                        </h2>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[var(--bg-hover)] text-[var(--text-secondary)] font-mono">
                            {filteredSites.length}
                        </span>
                    </div>
                    
                    {filter !== 'all' && (
                        <motion.button
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            onClick={() => handleFilterChange('all')}
                            className="text-[10px] px-2 py-1 rounded-full bg-[var(--brand-dim)] text-[var(--brand)] 
                                     hover:bg-[var(--brand-dim-md)] transition-colors"
                        >
                            Сбросить фильтр ×
                        </motion.button>
                    )}
                </div>

                <SitesTable
                    sites={filteredSites}
                    filter={filter}
                    onFilterChange={handleFilterChange}
                />
            </motion.section>

            <motion.section 
                variants={itemVariants} 
                className="mt-6"
                aria-label="Активные инциденты"
            >
                <IncidentsList incidents={activeIncidents} />
            </motion.section>

            <motion.footer 
                variants={fadeInVariants}
                className="px-[18px] mt-8 pt-4 border-t border-[var(--border)]"
            >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-[11px] text-[var(--text-muted)] font-mono">
                    <div className="flex items-center gap-2">
                        <span 
                            className={`w-2 h-2 rounded-full ${activeIncidents.length > 0 ? 'bg-[var(--status-warn)]' : 'bg-[var(--status-up)]'}`} 
                        />
                        <span>
                            {activeIncidents.length > 0 
                                ? `${activeIncidents.length} активных инцидентов` 
                                : 'Все системы работают'}
                        </span>
                    </div>
                    <div>
                        Обновлено: {currentTime}
                    </div>
                </div>
            </motion.footer>

            <ToastContainer toasts={toasts} onRemove={removeToast} />
        </motion.div>
    );
};